import React, { createContext, useContext } from 'react';
import type { FC, ReactNode } from 'react';
import { Spinner } from '@wix/cli-ui-kit';
import type { DevMetadata } from '@wix/cli-astro-definitions';
import { useDevCenterUpdater } from './hooks/useDevCenterUpdater.js';
import { TunnelingConsent } from './TunnelingConsent.js';
import type { DevCenterWatcher } from './utils/DevCenterWatcher.js';

interface DevEnvironment {
  baseUrl: string;
  devArmTag: string;
  devCenterWatcher: DevCenterWatcher;
}

const DevEnvironmentContext = createContext<DevEnvironment | null>(null);

interface DevEnvironmentProviderProps {
  serverLocalUrl: string;
  serverExposedUrl?: string;
  devMetadata: DevMetadata | null;
  children: ReactNode;
}

export const DevEnvironmentProvider: FC<DevEnvironmentProviderProps> = ({
  serverLocalUrl,
  serverExposedUrl,
  devMetadata,
  children,
}) => {
  const { baseUrl, tunnelRequired, devArmTag, devCenterWatcher } =
    useDevCenterUpdater({
      serverLocalUrl,
      serverExposedUrl,
      devMetadata,
    });

  return (
    <TunnelingConsent required={tunnelRequired}>
      {devArmTag == null ? (
        <Spinner text="Preparing your dashboard..." />
      ) : (
        <DevEnvironmentContext.Provider
          value={{ baseUrl, devArmTag, devCenterWatcher }}
        >
          {children}
        </DevEnvironmentContext.Provider>
      )}
    </TunnelingConsent>
  );
};

export const useDevEnvironment = (): DevEnvironment => {
  const ctx = useContext(DevEnvironmentContext);
  if (!ctx) {
    throw new Error(
      'useDevEnvironment must be used inside DevEnvironmentProvider'
    );
  }
  return ctx;
};
